/**
 * RefundReceiptPanel — what the refund drawer becomes once the server has
 * answered (BON-147, BON-335).
 *
 * Split out of `RefundBookingDrawer` because the two are genuinely different
 * screens sharing a frame: one collects a reason and can be cancelled, the
 * other reports an irreversible outcome and can only be acknowledged. Keeping
 * them in one file pushed it past the max-lines cap, and the seam was already
 * there.
 *
 * Five outcomes. Four have to read as *settled* rather than as errors: the
 * card was issued and emailed; the card was issued but the email did not go
 * out (the booking is flagged for review, and the operator has to be told the
 * money DID move); a refund to the original card or another rail was recorded
 * — nothing minted, nothing emailed, the money already moved by hand before
 * this screen existed; and the booking was already refunded, which the API
 * reports as a 409 and which is a statement of fact, not a failure. The fifth,
 * `mismatch`, is the one that must NOT read as settled (BON-335 review A1) —
 * the server processed a different method than the CSR requested, which is
 * unrecoverable once it happens, so this is the one place in the panel that
 * uses the assertive `alert` role instead of the polite `status` register.
 */
import type { BookingDetail, RefundBookingResult, RefundMethod } from '../../api/bookingsV2';

import { formatCents, seatsNote, customerDisplayName } from './bookingDetailModel';
import { moneyRow, refundMethodLabel } from './refundMoneyRows';
import type { IssuedRefund, RefundOutcome } from './useRefundBookingSubmit';

/** The drawer's heading for a given outcome. A plain function, not a component
 *  — the header lives in the drawer's own frame and only needs the string. */
export function receiptTitle(outcome: RefundOutcome): string {
  if (outcome.kind === 'settled') return 'Already refunded';
  if (outcome.kind === 'mismatch') return 'Refund mismatch — needs escalation';
  if (outcome.kind === 'recorded') return 'Refund recorded';
  if (outcome.kind === 'passes') return 'Gift passes issued';
  return outcome.refund.emailSent ? 'Gift card sent' : 'Gift card issued';
}

function issuedReceipt(booking: BookingDetail, refund: IssuedRefund) {
  const amount = formatCents(refund.amountCents);
  const name = customerDisplayName(booking);

  if (!refund.emailSent) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          A {amount} gift card (ending {refund.giftCard.codeMasked}) was issued, but the email to{' '}
          {name} did not go out. This booking has been flagged for review.
        </div>
        {moneyRow('Gift card value', amount, true)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-moss-400 bg-moss-50/60 px-4 py-3 text-sm text-moss-900 dark:border-moss-700 dark:bg-moss-900/10 dark:text-moss-200">
        {name} should be receiving an email with their {amount} gift card (ending{' '}
        {refund.giftCard.codeMasked}). It can be used on any workshop.
      </div>
      {moneyRow('Gift card value', amount, true)}
      <p className="text-xs text-stone-500 dark:text-stone-400">
        {seatsNote(booking.money.ticketsCount)}
      </p>
    </div>
  );
}

/** Refunded to gift passes: one per seat, emailed together. Codes are masked;
 *  the customer's email is the only place the full codes appear. */
function passesReceipt(booking: BookingDetail, refund: RefundBookingResult['refund']) {
  const passes = refund.giftPasses ?? [];
  const noun = passes.length === 1 ? 'gift pass' : 'gift passes';
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-moss-400 bg-moss-50/60 px-4 py-3 text-sm text-moss-900 dark:border-moss-700 dark:bg-moss-900/10 dark:text-moss-200">
        {passes.length} {noun} issued. {customerDisplayName(booking)} will get one email with every
        code. Each pass is good for one workshop admission.
      </div>
      <ul aria-label="Gift passes issued" className="space-y-1">
        {passes.map((pass) => (
          <li key={pass.id} className="font-mono text-sm text-bark-900 dark:text-stone-100">
            {pass.codeMasked}
          </li>
        ))}
      </ul>
      <p className="text-xs text-stone-500 dark:text-stone-400">
        {seatsNote(booking.money.ticketsCount)}
      </p>
    </div>
  );
}

/** A refund to the original card or another rail: no gift card, no email —
 *  Nebari recorded a payment that already happened elsewhere. */
function recordedReceipt(booking: BookingDetail, refund: RefundBookingResult['refund']) {
  const amount = formatCents(refund.amountCents);
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-moss-400 bg-moss-50/60 px-4 py-3 text-sm text-moss-900 dark:border-moss-700 dark:bg-moss-900/10 dark:text-moss-200">
        A {amount} refund to {refundMethodLabel(refund.method)} was recorded for this booking.
        Nebari did not move any money — this only records that it already happened.
      </div>
      {moneyRow('Refund recorded', amount, true)}
      {refund.externalReference && moneyRow('Reference', refund.externalReference)}
      <p className="text-xs text-stone-500 dark:text-stone-400">
        {seatsNote(booking.money.ticketsCount)}
      </p>
    </div>
  );
}

/** BON-335 review A1: the API and this app deploy as separate Railway
 *  services with no ordering guarantee, and a stale API can silently strip
 *  `method` and fall through to the gift-card mint. If that happens, the
 *  booking really is REFUNDED server-side (hence still showing the amount and
 *  any minted card so staff can act), but this must never look like a normal
 *  receipt — `refunds_immutable` and `UNIQUE(booking_id)` mean there is no
 *  in-app path to correct it. */
function mismatchReceipt(
  booking: BookingDetail,
  requested: RefundMethod,
  refund: RefundBookingResult['refund'],
) {
  const amount = formatCents(refund.amountCents);
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/20 dark:text-red-200">
        <p className="font-semibold">This refund did not go where it was told to.</p>
        <p className="mt-1">
          You asked to record a {amount} refund to {refundMethodLabel(requested)}, but the system
          processed it as {refundMethodLabel(refund.method)} instead
          {refund.method === 'GIFT_CARD' && refund.giftCard
            ? ` — a gift card ending ${refund.giftCard.codeMasked} may already have been emailed to the customer`
            : ''}
          {refund.method === 'GIFT_PASS'
            ? ' — gift passes may already be on their way to the customer'
            : ''}
          .
        </p>
        <p className="mt-2 font-semibold">
          Do not tell the customer this is resolved. Stop and escalate to engineering now with this
          booking&rsquo;s id ({booking.id}) — it cannot be corrected from here.
        </p>
      </div>
    </div>
  );
}

function receiptBody(booking: BookingDetail, outcome: RefundOutcome) {
  if (outcome.kind === 'settled') {
    return (
      <div className="rounded-lg bg-stone-50 px-4 py-3 text-sm text-stone-600 dark:bg-[#1a201d] dark:text-stone-300">
        {/* Destination-neutral (B7): ALREADY_REFUNDED is reachable from any
            of the three destinations, and the 409 body carries no method, so
            this copy cannot name — or deny — a gift card specifically. */}
        This booking has already been refunded — nothing further was recorded.
      </div>
    );
  }
  if (outcome.kind === 'mismatch') {
    return mismatchReceipt(booking, outcome.requested, outcome.refund);
  }
  if (outcome.kind === 'recorded') {
    return recordedReceipt(booking, outcome.refund);
  }
  if (outcome.kind === 'passes') {
    return passesReceipt(booking, outcome.refund);
  }
  return issuedReceipt(booking, outcome.refund);
}

export function RefundReceiptPanel({
  booking,
  outcome,
  onDone,
}: {
  booking: BookingDetail;
  outcome: RefundOutcome;
  onDone: () => void;
}) {
  // `mismatch` is the one outcome that is NOT a settled report — it is an
  // active incident the CSR must act on immediately, so it gets the
  // assertive `alert` register instead of the polite `status` every other
  // outcome uses.
  const isMismatch = outcome.kind === 'mismatch';
  return (
    <>
      {/* The outcome replaces the form in place, so assistive tech gets no
          navigation event to announce it. `status`/polite is the register for
          every outcome except `mismatch`: the money has already moved, so
          this is ordinarily a report, not an alert the user must interrupt
          their reading for. */}
      <div
        role={isMismatch ? 'alert' : 'status'}
        aria-live={isMismatch ? 'assertive' : 'polite'}
        className="flex-1 space-y-5 overflow-y-auto px-6 py-6"
      >
        {receiptBody(booking, outcome)}
      </div>
      <div className="flex items-center justify-end border-t border-stone-200 px-6 py-4 dark:border-stone-700">
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg bg-moss-600 px-4 py-2 text-sm font-semibold text-white hover:bg-moss-700"
        >
          Done
        </button>
      </div>
    </>
  );
}
